import React, { useState, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import religiousLife from '../cms/religious-life.json';
import pageIntros from '../cms/page-intros.json';
import PageChrome from '../components/layout/PageChrome';
import ReligiousLifeCard from '../components/cards/ReligiousLifeCard';
import RichText from '../components/ui/RichText';
import { getGoogleMapsLanguageParam } from '../utils/language';
import { renderTextWithEmailLinks } from '../utils/textFormatting';
import { FaExternalLinkAlt, FaMapMarkedAlt, FaClock, FaEnvelope, FaStarOfDavid, FaHome, FaUtensils, FaBook, FaLightbulb } from 'react-icons/fa';
import '../css/pages/ReligiousLife.css';

const intro = pageIntros.religiousLife || {};

const ReligiousLife = () => {
  const [selectedSection, setSelectedSection] = useState(null);
  const [searchParams] = useSearchParams();

  const closeModal = () => {
    setSelectedSection(null);
  };

  // Memoize the sections array to prevent unnecessary re-renders
  const sections = useMemo(() => [
    {
      type: 'ou-jlic',
      title: religiousLife['ou-jlic'].title,
      subtitle: religiousLife['ou-jlic'].subtitle,
      highlights: religiousLife['ou-jlic'].sections[0].highlights,
      data: religiousLife['ou-jlic'],
      icon: FaStarOfDavid
    },
    {
      type: 'minyan',
      title: religiousLife['minyan'].title,
      subtitle: religiousLife['minyan'].subtitle,
      highlights: religiousLife['minyan'].highlights.slice(0, 2),
      data: religiousLife['minyan'],
      icon: FaBook
    },
    {
      type: 'eruv',
      title: religiousLife['eruv'].title,
      subtitle: religiousLife['eruv'].subtitle,
      highlights: religiousLife['eruv'].sections[0].highlights,
      data: religiousLife['eruv'],
      icon: FaMapMarkedAlt
    },
    {
      type: 'housing',
      title: religiousLife['housing'].title,
      subtitle: religiousLife['housing'].subtitle,
      highlights: religiousLife['housing'].sections[0].highlights,
      data: religiousLife['housing'],
      icon: FaHome
    },
    {
      type: 'kosher-dining',
      title: religiousLife['kosher-dining'].title,
      subtitle: religiousLife['kosher-dining'].subtitle,
      highlights: religiousLife['kosher-dining'].sections[0].highlights,
      data: religiousLife['kosher-dining'],
      icon: FaUtensils
    },
    {
      type: 'shabbat-meals',
      title: religiousLife['shabbat-meals'].title,
      subtitle: religiousLife['shabbat-meals'].subtitle,
      highlights: religiousLife['shabbat-meals'].sections[0].highlights,
      data: religiousLife['shabbat-meals'],
      icon: FaLightbulb
    }
  ], []);

  // Handle URL parameters for specific sections
  useEffect(() => {
    const sectionParam = searchParams.get('section');
    if (sectionParam) {
      const section = sections.find(s => s.type === sectionParam);
      if (section) {
        setSelectedSection(section);
      }
    }
  }, [searchParams, sections]);

  return (
    <>
      <PageChrome
        title={intro.title}
        subtitle={intro.subtitle}
        sectionTitle={intro.sectionTitle}
        intro={intro.intro}
      >
        <section className="religious-life-page-section">
          <div className="religious-life-page-grid">
            {sections.map((section, index) => (
              <div 
                key={index}
                className="religious-life-page-card-wrapper"
              >
                <ReligiousLifeCard 
                  section={section}
                  onClick={() => setSelectedSection(section)}
                />
              </div>
            ))}
          </div>
        </section>
      </PageChrome>

      {/* Religious Life Modal */}
      {selectedSection && (
        <div className="religious-life-modal-overlay" onClick={closeModal}>
          <div className="religious-life-modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="religious-life-modal-close" onClick={closeModal}>×</button>
            <div className="religious-life-modal-header">
              <h2 className="religious-life-modal-title">{selectedSection.data.title}</h2>
              <p className="religious-life-modal-subtitle">{selectedSection.data.subtitle}</p>
            </div>
            <div className="religious-life-modal-body">
              <RichText value={selectedSection.data.description} className="religious-life-modal-description" />
              
              {/* OU-JLIC Modal Content */}
              {selectedSection.type === 'ou-jlic' && (
                <div className="religious-life-modal-sections">
                  {selectedSection.data.sections.map((section, index) => (
                    <div key={index} className="religious-life-modal-section">
                      <h4 className="religious-life-modal-section-title">{section.title}</h4>
                      <RichText value={section.content} className="religious-life-modal-section-content" />
                      {section.highlights && (
                        <ul className="religious-life-modal-highlights">
                          {section.highlights.map((highlight, idx) => (
                            <li key={idx}>{highlight}</li>
                          ))}
                        </ul>
                      )}
                      {section.externalLink && (
                        <div className="religious-life-modal-external-link">
                          <a 
                            href={section.externalLink.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="religious-life-modal-link"
                          >
                            {section.externalLink.text} <FaExternalLinkAlt />
                          </a>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {/* Eruv Modal Content */}
              {selectedSection.type === 'eruv' && (
                <div className="religious-life-modal-sections">
                  {selectedSection.data.sections.map((section, index) => (
                    <div key={index} className="religious-life-modal-section">
                      <h4 className="religious-life-modal-section-title">{section.title}</h4>
                      <RichText value={section.content} className="religious-life-modal-section-content" />
                      {section.highlights && (
                        <ul className="religious-life-modal-highlights">
                          {section.highlights.map((highlight, idx) => (
                            <li key={idx}>{highlight}</li>
                          ))}
                        </ul>
                      )}
                      {section.externalLink && (
                        <div className="religious-life-modal-external-link">
                          <a 
                            href={section.externalLink.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="religious-life-modal-link"
                          >
                            {section.externalLink.text} <FaExternalLinkAlt />
                          </a>
                        </div>
                      )}
                      {section.mapInfo && (
                        <div className="religious-life-modal-map-section">
                          <h5 className="religious-life-modal-map-title">
                            <FaMapMarkedAlt /> {section.mapInfo.title}
                          </h5>
                          <RichText value={section.mapInfo.description} className="religious-life-modal-map-description" />
                          <div className="religious-life-modal-map-container">
                            <iframe
                              src={`${section.mapInfo.mapUrl}${getGoogleMapsLanguageParam()}`}
                              width="100%"
                              height="300"
                              className="religious-life-modal-map"
                              allowFullScreen=""
                              loading="lazy"
                              referrerPolicy="no-referrer-when-downgrade"
                              title={section.mapInfo.title}
                            ></iframe>
                          </div>
                          <div className="religious-life-modal-contact-info">
                            <RichText
                              value={section.mapInfo.contact.note}
                              className="religious-life-modal-contact-note"
                            />
                            <a 
                              href={`mailto:${section.mapInfo.contact.email}`}
                              className="religious-life-modal-contact-link"
                            >
                              <FaEnvelope /> {section.mapInfo.contact.name}
                            </a>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {/* Housing Modal Content */}
              {selectedSection.type === 'housing' && (
                <div className="religious-life-modal-sections">
                  {selectedSection.data.sections.map((section, index) => (
                    <div key={index} className="religious-life-modal-section">
                      <h4 className="religious-life-modal-section-title">{section.title}</h4>
                      <RichText value={section.content} className="religious-life-modal-section-content" />
                      {section.highlights && (
                        <ul className="religious-life-modal-highlights">
                          {section.highlights.map((highlight, idx) => (
                            <li key={idx}>{highlight}</li>
                          ))}
                        </ul>
                      )}
                      {section.externalLink && (
                        <div className="religious-life-modal-external-link">
                          <a 
                            href={section.externalLink.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="religious-life-modal-link"
                          >
                            {section.externalLink.text} <FaExternalLinkAlt />
                          </a>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {/* Kosher Dining Modal Content */}
              {selectedSection.type === 'kosher-dining' && (
                <div className="religious-life-modal-sections">
                  {selectedSection.data.sections.map((section, index) => (
                    <div key={index} className="religious-life-modal-section">
                      <h4 className="religious-life-modal-section-title">{section.title}</h4>
                      <RichText value={section.content} className="religious-life-modal-section-content" />
                      {section.highlights && (
                        <ul className="religious-life-modal-highlights">
                          {section.highlights.map((highlight, idx) => (
                            <li key={idx}>{renderTextWithEmailLinks(highlight)}</li>
                          ))}
                        </ul>
                      )}
                      {section.externalLink && (
                        <div className="religious-life-modal-external-link">
                          <a 
                            href={section.externalLink.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="religious-life-modal-link"
                          >
                            {section.externalLink.text} <FaExternalLinkAlt />
                          </a>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {/* Shabbat Meals Modal Content */}
              {selectedSection.type === 'shabbat-meals' && (
                <div className="religious-life-modal-sections">
                  {selectedSection.data.sections.map((section, index) => (
                    <div key={index} className="religious-life-modal-section">
                      <h4 className="religious-life-modal-section-title">{section.title}</h4>
                      <RichText value={section.content} className="religious-life-modal-section-content" />
                      {section.highlights && (
                        <ul className="religious-life-modal-highlights">
                          {section.highlights.map((highlight, idx) => (
                            <li key={idx}>{highlight}</li>
                          ))}
                        </ul>
                      )}
                      {section.externalLink && (
                        <div className="religious-life-modal-external-link">
                          <a 
                            href={section.externalLink.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="religious-life-modal-link"
                          >
                            {section.externalLink.text} <FaExternalLinkAlt />
                          </a>
                        </div>
                      )}
                      {section.links && (
                        <div className="religious-life-modal-links">
                          {section.links.map((link, idx) => (
                            <a 
                              key={idx}
                              href={link.url}
                              className="religious-life-modal-link"
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              {link.text} <FaExternalLinkAlt />
                            </a>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {/* Minyan Modal Content */}
              {selectedSection.type === 'minyan' && (
                <div className="religious-life-modal-sections">
                  <div className="religious-life-modal-section">
                    <h4 className="religious-life-modal-section-title">Location</h4>
                    <p className="religious-life-modal-section-content">{selectedSection.data.location}</p>
                  </div>
                  
                  <div className="religious-life-modal-section">
                    <h4 className="religious-life-modal-section-title">Schedule</h4>
                    <div className="religious-life-modal-schedule">
                      {selectedSection.data.schedule.map((item, index) => (
                        <div key={index} className="religious-life-modal-schedule-item">
                          <span className="religious-life-modal-schedule-day">{item.day}</span>
                          <span className="religious-life-modal-schedule-time">
                            <FaClock /> {item.time}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="religious-life-modal-section">
                    <h4 className="religious-life-modal-section-title">Contact</h4>
                    <div className="religious-life-modal-contact-info">
                      <div className="religious-life-modal-contact-note">
                      <RichText value={selectedSection.data.contact.note} />
                    </div>
                      <a 
                        href={`mailto:${selectedSection.data.contact.email}`}
                        className="religious-life-modal-contact-link"
                      >
                        <FaEnvelope /> {selectedSection.data.contact.name} - {selectedSection.data.contact.title}
                      </a>
                    </div>
                  </div>

                  <div className="religious-life-modal-section">
                    <h4 className="religious-life-modal-section-title">Highlights</h4>
                    <ul className="religious-life-modal-highlights">
                      {selectedSection.data.highlights.map((highlight, index) => (
                        <li key={index}>{highlight}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ReligiousLife; 